import { Phone as PhoneIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from "@mui/material";

import { RISK_COLORS } from "../common/colors";
import { type ProtocolPerformance } from "../types/metrics";
import { CONVERSATION_LENS } from "../utils/terminology";
import { TimeRange } from "../utils/timerange";
import { ScoreBar } from "./ScoreBar";

interface ProtocolPerformanceTableProps {
  timeRange: TimeRange;
  timezone: string;
  user: string;
  data?: ProtocolPerformance[];
  onClick?: (key: string, value: string) => void;
  isLoading?: boolean;
}

interface RiskDistributionBarProps {
  distribution: {
    low: number;
    medium: number;
    high: number;
  };
  width?: number;
  height?: number;
}

const RiskDistributionBar = ({ distribution, width = 100, height = 16 }: RiskDistributionBarProps) => {
  // Convert distribution to percentages
  const lowPercentage = Math.round(distribution.low * 100);
  const mediumPercentage = Math.round(distribution.medium * 100);
  const highPercentage = Math.round(distribution.high * 100);

  // Ensure percentages sum to 100 (handle rounding errors)
  const total = lowPercentage + mediumPercentage + highPercentage;
  let adjustedLow = lowPercentage;
  let adjustedMedium = mediumPercentage;
  let adjustedHigh = highPercentage;

  if (total !== 100 && total > 0) {
    // Adjust the largest value to make total 100
    if (lowPercentage >= mediumPercentage && lowPercentage >= highPercentage) {
      adjustedLow = 100 - mediumPercentage - highPercentage;
    } else if (mediumPercentage >= lowPercentage && mediumPercentage >= highPercentage) {
      adjustedMedium = 100 - lowPercentage - highPercentage;
    } else {
      adjustedHigh = 100 - lowPercentage - mediumPercentage;
    }
  }

  return (
    <Tooltip title={`Low: ${adjustedLow}%, Medium: ${adjustedMedium}%, High: ${adjustedHigh}%`}>
      <Box sx={{
        display: "flex",
        width: `${width}px`,
        height: `${height}px`,
        border: "1px solid #ccc",
        borderRadius: 1,
        overflow: "hidden"
      }}>
        {adjustedLow > 0 && (
          <Box sx={{
            width: `${adjustedLow}%`,
            height: "100%",
            backgroundColor: RISK_COLORS.low.background
          }} />
        )}
        {adjustedMedium > 0 && (
          <Box sx={{
            width: `${adjustedMedium}%`,
            height: "100%",
            backgroundColor: RISK_COLORS.medium.background
          }} />
        )}
        {adjustedHigh > 0 && (
          <Box sx={{
            width: `${adjustedHigh}%`,
            height: "100%",
            backgroundColor: RISK_COLORS.high.background
          }} />
        )}
      </Box>
    </Tooltip>
  );
};

const ProtocolPerformanceTable = ({ timeRange: _timeRange, timezone: _timezone, user: _user, data = [], onClick, isLoading = false }: ProtocolPerformanceTableProps) => {
  const protocolData = data;

  const onProtocolClick = (protocol: ProtocolPerformance) => {
    if (onClick) {
      onClick("sc_name", protocol.name);
    }
  };

  if (isLoading) {
    return (
      <Paper sx={{ p: 2, display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (protocolData.length === 0) {
    return (
      <Paper sx={{ p: 2 }}>
        <Alert severity="info">No {CONVERSATION_LENS} data available for the selected time range.</Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <PhoneIcon sx={{ mr: 1 }} />
        <Typography variant="h6">{CONVERSATION_LENS} Performance</Typography>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{CONVERSATION_LENS}</TableCell>
              <TableCell align="right">% of Conversations</TableCell>
              <TableCell>Average Score</TableCell>
              <TableCell>Risk Distribution</TableCell>
              <TableCell align="right"># Scored</TableCell>
              <TableCell align="right"># No Score</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {protocolData.map((protocol) => (
              <TableRow key={protocol.name} hover onClick={() => onProtocolClick(protocol)}>
                <TableCell component="th" scope="row">
                  {protocol.name}
                </TableCell>
                <TableCell align="right">
                  {protocol.percentOfTotal.toFixed(1)}%
                </TableCell>
                <TableCell>
                  <ScoreBar score={protocol.averageScore} />
                </TableCell>
                <TableCell>
                  <RiskDistributionBar distribution={protocol.riskDistribution} />
                </TableCell>
                <TableCell align="right">
                  {protocol.scoredCount.toLocaleString()}
                </TableCell>
                <TableCell align="right">
                  {(protocol.conversationCount - protocol.scoredCount).toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ProtocolPerformanceTable;
